import React, { useCallback, useEffect, useRef, useState } from "react"
import styles from "./callLog.module.css"
import Table from "../../components/Table"
import api from "../../services/api"
import { toast } from "react-toastify"
import Select from "../../components/Form/Select"
import { formatCurrency, maskPhone } from "../../utils/formatter"
import { MdCall, MdOutlineFilterAltOff, MdOutlinePlayCircleFilled, MdSearch } from "react-icons/md"
import DatePicker from './../../components/Form/DatePicker/index'
import Button from "../../components/Form/Button"
import Loading from './../layouts/Loading/index'
import Card from "../Dashboard/Card"
import { useData } from "../../contexts/dataContext"
import { useCrm } from "../../contexts/crmContext"
import BackgroundSelectFilter from "../../components/BackgroundSelectFilter"
import ModalInfo from "../../components/Modal/ModalInfo"
import { adjustTextByIndentation, getOptionByOptionList, getUsersBySelectedTeams, getValidUserIdsByTeams } from "../../utils/utils"
import { useParams } from "react-router-dom"

const optionsStatus = [
    { id: "answer", value: "Atendida" },
    { id: "no_answer", value: "Caixa Postal" },
    { id: "rejected", value: "Chamada Recusada" },
    { id: "canceled", value: "Ligação Cancelada" },
    { id: "busy", value: "Número Ocupado" },
    { id: "invalid_number", value: "Número Inválido" },
]

const initialFilters = {
    dateStart: "",
    dateEnd: "",
    teams: [],
    users: [],
    flags: [],
    status: [],
}

const CallLog = () => {
    const [calls, setCalls] = useState([])
    const [cards, setCards] = useState([])
    const [maxCalls, setMaxCalls] = useState(0)
    const [page, setPage] = useState(1)
    const [filters, setFilters] = useState(initialFilters)
    const [isLoading, setIsLoading] = useState(false)
    const [loadMore, setLoadMore] = useState(false)
    const [hasLoaded, setHasLoaded] = useState(false)
    const [backgroundFilter, setBackgroundFilter] = useState(true)
    const [modalTranscription, setModalTranscription] = useState(false)
    const [descriptionTranscription, setDescriptionTranscription] = useState("")
    const [contentTranscription, setContentTranscription] = useState("")

    const isLoadingCall = useRef(false)

    const { account_id } = useParams()
    const { openModalLead } = useCrm()
    const { teams, users, flagsByoptions } = useData()

    const headers = [
        "#",
        "Data",
        "Celular",
        "Status",
        "Duração",
        "Valor da Ligação",
        "Valor da Transcrição",
        "Valor Total",
        "Responsável",
        "Gravação",
        "",
    ]

    useEffect(() => {
        if (loadMore) {
            getAllCallLogByFilters()
        }
    }, [loadMore])

    const buildQueryString = (filtersToApply = {}) => {
        const params = new URLSearchParams()
        Object.entries(filtersToApply).forEach(([key, value]) => {
            if (Array.isArray(value) && value.length > 0) {
                params.append(key, value.join(","))
            } else if (!Array.isArray(value) && value) {
                params.append(key, value)
            }
        })
        return params.toString()
    }

    const getAllCallLogByFilters = async (newFind = false, filtersToApply = filters) => {
        try {
            if (!isLoadingCall.current) {
                isLoadingCall.current = true
                if (newFind) {
                    setIsLoading(true)
                }
                const queryString = buildQueryString(filtersToApply)
                const response = await api.get(
                    `/voip/log?${queryString ? `${queryString}&` : ""}page=${newFind ? 1 : page}`,
                    account_id ? { headers: { "x-api-key": account_id } } : undefined
                )
                const calls = response.data?.data?.calls || []

                if (newFind) {
                    const cards = response.data?.data?.cards || []
                    const maxCalls = response.data?.data?.maxCalls || 0
                    setCalls(calls)
                    setCards(cards)
                    setMaxCalls(maxCalls)
                } else {
                    setCalls(previousValue => {
                        return [...previousValue, ...calls]
                    })
                }
                setIsLoading(false)
                setHasLoaded(true)
                setPage(prev => newFind ? 2 : prev + 1)
                setLoadMore(false)
                setBackgroundFilter(false)
                isLoadingCall.current = false
            }
        } catch (err) {
            setIsLoading(false)
            setHasLoaded(true)
            setLoadMore(false)
            toast.error(err?.response?.data?.message)
            isLoadingCall.current = false
        }
    }

    const getDataNewCall = (newCall) => {
        return {
            id: newCall._id,
            value: [
                newCall.created_at ?? "-",
                maskPhone(newCall.phone) ?? "-",
                getOptionByOptionList(newCall.status, optionsStatus) ?? "-",
                newCall.duration_formatted ?? "00:00:00",
                formatCurrency(newCall.cost_call ?? 0) ?? "R$ 0,00",
                formatCurrency(newCall.cost_transcription ?? 0) ?? "R$ 0,00",
                formatCurrency(newCall.cost_total ?? 0) ?? "R$ 0,00",
                newCall.user_created ?? "-",
                newCall.url ? <a href={newCall.url} target="_blank" className={styles.link} rel="noreferrer" onClick={(e) => e.stopPropagation()}><MdOutlinePlayCircleFilled /></a> : "-",
            ],
            actions: [
                {
                    label: "Abrir Lead",
                    onClick: handleOpenModalLead
                },
                {
                    label: "Resumo e Transcrição",
                    onClick: handleOpenModalTranscription
                }
            ]
        }
    }

    const getFilteredValues = () => {
        const values = calls
        values.sort((a, b) => {
            if (a.created_at < b.created_at) {
                return 1
            } else if (a.created_at > b.created_at) {
                return -1
            } else {
                return 0
            }
        })

        const filteredValues = values?.map((call) => getDataNewCall(call))
        return filteredValues
    }

    const handleChangeFilters = (name, value) => {
        if (name === "period") {
            const dateStart = value.dateStart
            const dateEnd = value.dateEnd
            setFilters(previousValue => { return { ...previousValue, dateStart, dateEnd } })
        } else {
            setFilters(previousValue => ({
                ...previousValue,
                [name]: value,
                ...(name === "teams" ? { users: getValidUserIdsByTeams(previousValue.users, value, users, teams, [], previousValue.teams) } : {}),
            }))
        }
    }

    const handleApplyFilters = () => {
        getAllCallLogByFilters(true)
    }

    const handleClearFilters = () => {
        setFilters(initialFilters)
        setCalls([])
        setCards([])
        setMaxCalls(0)
        setPage(1)
        setHasLoaded(false)
        setBackgroundFilter(true)
    }

    const handleOpenModalLead = (callId) => {
        const call = calls?.find(call => call._id === callId)
        if (call) {
            const leadId = call.lead_id
            openModalLead(leadId)
        }
    }

    const handleOpenModalTranscription = (callId) => {
        const call = calls?.find(call => call._id === callId)
        if (call) {
            const transcription = call?.transcription
            if (transcription?.full_text || transcription?.summary_text) {
                const formattedText = formatContentByTranscription(transcription?.full_text, transcription?.summary_text)
                setDescriptionTranscription(`Resumo e transcrição da chamada realizada em ${call.created_at} para o número: ${maskPhone(call.phone)}`)
                setContentTranscription(formattedText)
                setModalTranscription(true)
            } else {
                toast.error("Nenhuma transcrição encontrada!")
            }
        }
    }

    const formatContentByTranscription = (fullText = "", sumaryText = "") => {
        return (
            <div className={styles.containerTranscription}>
                <div className={styles.groupContent}>
                    <span className={styles.title}>Resumo</span>
                    <div className={styles.content}>
                        {sumaryText ?
                            adjustTextByIndentation(sumaryText)
                            :
                            <span className={styles.notFound}>O resumo não foi encontrado!</span>
                        }
                    </div>
                </div>
                <div className={styles.groupContent}>
                    <span className={styles.title}>Transcrição Completa</span>
                    <div className={styles.content}>
                        {fullText ?
                            adjustTextByIndentation(fullText)
                            :
                            <span className={styles.notFound}>A transcrição não foi encontrada!</span>
                        }
                    </div>
                </div>
            </div>
        )
    }

    const handleCloseModalTranscription = () => {
        setModalTranscription(false)
    }

    const handleScrollTable = useCallback(() => {
        if (!loadMore && maxCalls > calls.length) {
            setLoadMore(true)
        }
    }, [maxCalls, calls, loadMore])

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerIcon}>
                    <MdCall />
                </div>
                <div>
                    <h2>Registro de Chamadas</h2>
                    <span>{hasLoaded ? `${maxCalls || calls.length} registro${(maxCalls || calls.length) === 1 ? "" : "s"} encontrado${(maxCalls || calls.length) === 1 ? "" : "s"}` : "Carregando registros"}</span>
                </div>
            </div>

            <div className={styles.filters}>
                <div className={styles.filterFields}>
                    <Select multiple={true} name="Equipe" placeholder="Buscar..." options={teams} onChange={(value) => handleChangeFilters("teams", value)} selected={filters.teams ?? []} required={true} />
                    <Select multiple={true} name="Usuário" placeholder="Buscar..." options={getUsersBySelectedTeams(filters.teams, users, teams)} onChange={(value) => handleChangeFilters("users", value)} selected={filters.users ?? []} required={true} />
                    <Select multiple={true} name="Etiquetas" placeholder="Buscar..." options={flagsByoptions} onChange={(value) => handleChangeFilters("flags", value)} selected={filters.flags || []} required={true} />
                    <Select multiple={true} name="Status" placeholder="Buscar..." options={optionsStatus} onChange={(value) => handleChangeFilters("status", value)} selected={filters.status ?? []} required={true} />
                    <DatePicker multiple={true} name="Período" dateStart={filters.dateStart ?? ""} dateEnd={filters.dateEnd ?? ""} onChange={(dateStart, dateEnd) => handleChangeFilters("period", { dateStart, dateEnd })} />
                </div>
                <div className={styles.filterActions}>
                    <Button variant="primary" onClick={handleApplyFilters} disabled={isLoading}><MdSearch /> Filtrar</Button>
                    <Button className={styles.clearButton} onClick={handleClearFilters} disabled={isLoading}><MdOutlineFilterAltOff /> Limpar</Button>
                </div>
            </div>
            <div className={styles.cards}>
                {cards?.length > 0 && cards.map(item => (
                    <Card
                        key={item.id}
                        icon=""
                        name={item.name}
                        value={item.value}
                        variation={item.variation}
                        is_positive={item.is_positive}
                        type={item.id === "answeredCalls" ? "success" : (item.id === "investmentCalls" ? "warning" : "info")}
                    />
                ))}
            </div>
            {!backgroundFilter &&
                <Table
                    headers={headers}
                    values={getFilteredValues()}
                    onClick={(id) => handleOpenModalLead(id)}
                    onScroll={handleScrollTable}
                    loadMore={loadMore}
                    messageNotRegisters={hasLoaded ? "Nenhum registro encontrado" : "Carregando registros"}
                    showIndex={true}
                    showCount={true}
                    count={maxCalls}
                />
            }
            {backgroundFilter &&
                <BackgroundSelectFilter
                    title="Filtre antes de carregar"
                    text="Escolha o período, equipes, usuários, etiquetas ou status para buscar somente os registros necessários."
                />
            }
            {modalTranscription &&
                <ModalInfo
                    title="Resumo e Transcrição"
                    description={descriptionTranscription}
                    content={contentTranscription}
                    closeModal={handleCloseModalTranscription}
                />
            }
            {isLoading && <Loading fullPage={true} />}
        </div>
    )
}

export default CallLog
